import { useId } from "react";

import {
  cardSourceBgHex,
  featuredTitleTextClass,
  firstSentence,
  variantBgClass,
  variantForSlot,
  variantPlusClass,
  type CardVariant,
} from "@/lib/cardVariants";
import { rewritePulseImageUrl } from "@/lib/pulseImage";
import { toSafeHttpUrl } from "@/lib/media";
import type { PulseCard } from "@/types";

import ImagePlaceholder from "../ImagePlaceholder";
import VideoPicto, { hasVerifiedVideo } from "./VideoPicto";

export type CardLayout = "featured" | "featured-next" | "duo" | "headline" | "layered-bottom";

const PLUS_SVG_COLORS: Record<CardVariant, { circle: string; stroke: string }> = {
  blue: { circle: "#0E9FEE", stroke: "#003F28" },
  "green-dark": { circle: "#003020", stroke: "#FFFFFF" },
  green: { circle: "#00A443", stroke: "#003F28" },
  orange: { circle: "#D68010", stroke: "#003F28" },
  white: { circle: "#0E9FEE", stroke: "#003F28" },
};

/** Plus en cards duo — Figma 1513:21918 (white), 1126:5508 (blue). */
const DUO_SLOTS = [
  {
    bg: "bg-white",
    title: "text-[#003F28]",
    summary: "text-[#003F28]",
    plus: { circle: "#F3ECE7", stroke: "#003F28" },
    mediaBg: "bg-[#F3ECE7]",
    showImage: true,
  },
  {
    bg: "bg-[#0AB2FE]",
    title: "text-[#003F28]",
    summary: "text-[#003F28]",
    plus: { circle: "#0E9FEE", stroke: "#003F28" },
    mediaBg: "",
    showImage: false,
  },
] as const;

function duoSlotIndex(slotIndex: number): number {
  return Math.max(0, Math.min(DUO_SLOTS.length - 1, slotIndex - 4));
}

/** Layout fijo layered-bottom — Figma 1126:5679, 1126:5680. */
const LAYERED_BOTTOM_SLOTS = [
  {
    bg: "bg-[#003F28]",
    title: "text-white",
    summary: "text-white",
    plus: { circle: "#003020", stroke: "#FFFFFF" },
    showImage: false,
  },
  {
    bg: "bg-[#F8DDC4]",
    title: "text-[#00402A]",
    summary: "text-[#00402A]",
    plus: { circle: "#FFFFFF", stroke: "#003F28" },
    showImage: true,
  },
] as const;

function layeredBottomIndex(slotIndex: number): number {
  return Math.max(0, Math.min(LAYERED_BOTTOM_SLOTS.length - 1, slotIndex - 7));
}

interface Props {
  card: PulseCard | null;
  slotIndex: number;
  layout: CardLayout;
  loading?: boolean;
  onSelect: (card: PulseCard, sourceBg: string) => void;
}

function PlusButton({
  variant,
  onClick,
  compact = false,
  layout,
  slotIndex = 0,
}: {
  variant: CardVariant;
  onClick: () => void;
  compact?: boolean;
  layout?: CardLayout;
  slotIndex?: number;
}) {
  const clipId = useId();

  const renderSvgPlus = (
    circle: string,
    stroke: string,
    positionClass: string,
  ) => (
    <button
      type="button"
      aria-label="Ver detalle"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`pulse-plus-btn absolute z-20 flex h-[56px] w-[56px] items-center justify-center rounded-full overflow-hidden transition hover:scale-105 ${positionClass}`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="56"
        height="56"
        viewBox="0 0 56 56"
        fill="none"
      >
        <g clipPath={`url(#${clipId})`}>
          <circle cx="28" cy="28" r="28" fill={circle} />
          <path d="M27.8906 17.78L27.8906 38.22" stroke={stroke} strokeWidth="2" />
          <path d="M38.2203 28.0278L17.7803 28.0278" stroke={stroke} strokeWidth="2" />
        </g>
        <defs>
          <clipPath id={clipId}>
            <rect width="56" height="56" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </button>
  );

  if (layout === "headline") {
    return renderSvgPlus("#D68010", "#003F28", "bottom-[30px] right-[30px]");
  }

  if (layout === "duo") {
    const config = DUO_SLOTS[duoSlotIndex(slotIndex ?? 0)]!;
    return renderSvgPlus(config.plus.circle, config.plus.stroke, "bottom-[30px] right-[30px]");
  }

  if (layout === "layered-bottom") {
    const config = LAYERED_BOTTOM_SLOTS[layeredBottomIndex(slotIndex)]!;
    return renderSvgPlus(config.plus.circle, config.plus.stroke, "bottom-[30px] right-[30px]");
  }

  if (compact || layout === "featured-next") {
    const { circle, stroke } = PLUS_SVG_COLORS[variant];
    return renderSvgPlus(circle, stroke, "bottom-5 right-5");
  }

  return (
    <button
      type="button"
      aria-label="Ver detalle"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`pulse-plus-btn absolute z-20 flex items-center justify-center rounded-full font-light leading-none transition ${variantPlusClass(variant)} ${
        compact
          ? "bottom-5 right-5 h-10 w-10 text-2xl"
          : "bottom-4 right-4 h-14 w-14 text-3xl"
      }`}
    >
      +
    </button>
  );
}

const TITLE_SIZE: Record<CardLayout, string> = {
  featured: "pulse-card-title pulse-card-title--featured",
  "featured-next": "pulse-card-title pulse-card-title--featured",
  duo: "pulse-card-title pulse-card-title--large",
  headline: "pulse-card-title pulse-card-title--large",
  "layered-bottom": "pulse-card-title pulse-card-title--large",
};

const LAYOUT_SHELL: Record<CardLayout, string> = {
  featured: "pulse-story-card--featured rounded-[20px] box-border",
  "featured-next": "pulse-story-card--featured rounded-[20px] box-border",
  duo: "pulse-story-card--duo rounded-[24px]",
  headline: "pulse-story-card--headline rounded-[24px]",
  "layered-bottom": "pulse-story-card--layered-bottom rounded-[24px]",
};

const EMPTY_SLOT_CLASS = "pulse-story-card--empty";

function verifiedVideoPicto(card: PulseCard) {
  return hasVerifiedVideo(card.video_status) ? <VideoPicto /> : null;
}

export default function PulseStoryCard({
  card,
  slotIndex,
  layout,
  loading = false,
  onSelect,
}: Props) {
  const variant = variantForSlot(layout, slotIndex);
  const imageUrl = card
    ? toSafeHttpUrl(rewritePulseImageUrl(card.image_url, slotIndex))
    : null;
  const showSummary = false;
  const isEmpty = !card;

  if (isEmpty) {
    if (layout === "featured" || layout === "featured-next") {
      return (
        <div
          className={`relative flex flex-col overflow-hidden p-5 ${variantBgClass(variant)} opacity-40 ${LAYOUT_SHELL[layout]} ${EMPTY_SLOT_CLASS}`}
          aria-hidden
        >
          <div className="h-[var(--pulse-card-featured-media-h)] w-full shrink-0 rounded-[15px] bg-white/10" />
        </div>
      );
    }

    const emptyBg =
      layout === "headline"
        ? "bg-[#FF9C1A]"
        : layout === "layered-bottom"
          ? LAYERED_BOTTOM_SLOTS[layeredBottomIndex(slotIndex)]!.bg
          : layout === "duo"
            ? DUO_SLOTS[duoSlotIndex(slotIndex)]!.bg
            : variantBgClass(variant);

    return (
      <div
        className={`relative overflow-hidden ${emptyBg} opacity-40 ${LAYOUT_SHELL[layout]} ${EMPTY_SLOT_CLASS}`}
        aria-hidden
      />
    );
  }

  const open = () => onSelect(card, cardSourceBgHex(layout, slotIndex));
  const duoIndex = slotIndex - 4;
  const layeredIndex = layeredBottomIndex(slotIndex);
  const showSummarySnippet =
    showSummary ||
    (layout === "duo" && duoIndex === 1) ||
    (layout === "layered-bottom" && layeredIndex === 0);
  const summarySnippet = showSummarySnippet ? firstSentence(card.summary) : null;

  if (layout === "featured" || layout === "featured-next") {
    const mediaRadius = layout === "featured-next" ? "rounded-[15px]" : "rounded-xl";

    return (
      <article
        role="button"
        tabIndex={0}
        aria-label={card.title}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        className={`pulse-story-card group relative flex cursor-pointer flex-col overflow-hidden p-5 ${variantBgClass(variant)} ${LAYOUT_SHELL[layout]} ${loading ? "is-pulse-loading" : ""}`}
      >
        {imageUrl ? (
          <div
            className={`pulse-card-media relative h-[var(--pulse-card-featured-media-h)] w-full shrink-0 overflow-hidden ${mediaRadius}`}
          >
            <img
              src={imageUrl}
              alt=""
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
            {verifiedVideoPicto(card)}
          </div>
        ) : (
          !loading && (
            <div className="relative shrink-0">
              <ImagePlaceholder
                src={null}
                className={`h-[var(--pulse-card-featured-media-h)] w-full ${mediaRadius} opacity-30`}
              />
              {verifiedVideoPicto(card)}
            </div>
          )
        )}
        {!imageUrl && loading ? verifiedVideoPicto(card) : null}

        <div className="relative mt-3 flex min-h-0 flex-1 flex-col pr-12">
          {card.is_update && (
            <span className="mb-2 inline-block w-fit rounded bg-[#003F28]/10 px-2 py-0.5 text-xs font-semibold text-[#003F28]">
              ACTUALIZACIÓN
            </span>
          )}
          <h3
            className={`font-medium ${TITLE_SIZE[layout]} ${featuredTitleTextClass(variant)}`}
          >
            {card.title}
          </h3>
        </div>

        <PlusButton variant={variant} onClick={open} compact layout={layout} />
      </article>
    );
  }

  if (layout === "headline") {
    return (
      <article
        role="button"
        tabIndex={0}
        aria-label={card.title}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        className={`pulse-story-card group relative cursor-pointer overflow-hidden bg-[#FF9C1A] ${LAYOUT_SHELL[layout]} ${loading ? "is-pulse-loading" : ""}`}
      >
        <div className="pulse-headline-copy z-10 max-w-[calc(100%-60px)]">
          {card.is_update && (
            <span className="mb-2 inline-block w-fit rounded bg-[#003F28]/10 px-2 py-0.5 text-xs font-semibold text-[#003F28]">
              ACTUALIZACIÓN
            </span>
          )}
          <h3
            className={`font-medium text-[#003F28] ${TITLE_SIZE.headline}`}
          >
            {card.title}
          </h3>
        </div>

        {imageUrl ? (
          <div className="pulse-headline-media pulse-card-media relative z-0 overflow-hidden rounded-[20px]">
            <img
              src={imageUrl}
              alt=""
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
            {verifiedVideoPicto(card)}
          </div>
        ) : (
          !loading && (
            <div className="relative z-0">
              <ImagePlaceholder
                src={null}
                className="pulse-headline-media rounded-[20px] opacity-30"
              />
              {verifiedVideoPicto(card)}
            </div>
          )
        )}
        {!imageUrl && loading ? verifiedVideoPicto(card) : null}

        <PlusButton variant={variant} onClick={open} layout={layout} />
      </article>
    );
  }

  if (layout === "layered-bottom") {
    const config = LAYERED_BOTTOM_SLOTS[layeredBottomIndex(slotIndex)]!;

    return (
      <article
        role="button"
        tabIndex={0}
        aria-label={card.title}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        className={`pulse-story-card group relative cursor-pointer overflow-hidden ${config.bg} ${LAYOUT_SHELL[layout]} ${loading ? "is-pulse-loading" : ""}`}
      >
        <div
          className={`pulse-layered-copy z-10 max-w-[calc(100%-60px)] ${config.title}`}
        >
          {card.is_update && (
            <span className="mb-2 inline-block w-fit rounded bg-white/20 px-2 py-0.5 text-xs font-semibold">
              ACTUALIZACIÓN
            </span>
          )}
          <h3 className={`font-medium ${TITLE_SIZE["layered-bottom"]}`}>{card.title}</h3>
        </div>

        {config.showImage &&
          (imageUrl ? (
            <div className="pulse-layered-media pulse-card-media relative z-0 max-w-[calc(100%-60px)] overflow-hidden rounded-[12px]">
              <img
                src={imageUrl}
                alt=""
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              />
              {verifiedVideoPicto(card)}
            </div>
          ) : (
            !loading && (
              <div className="relative z-0">
                <ImagePlaceholder
                  src={null}
                  className="pulse-layered-media max-w-[calc(100%-60px)] rounded-[12px] opacity-30"
                />
                {verifiedVideoPicto(card)}
              </div>
            )
          ))}
        {!config.showImage ? verifiedVideoPicto(card) : null}

        {summarySnippet && (
          <p className={`pulse-layered-summary z-10 ${config.summary}`}>{summarySnippet}</p>
        )}

        <PlusButton variant={variant} onClick={open} layout={layout} slotIndex={slotIndex} />
      </article>
    );
  }

  const duoConfig = DUO_SLOTS[duoSlotIndex(slotIndex)]!;

  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={card.title}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      className={`pulse-story-card group relative cursor-pointer overflow-hidden ${duoConfig.bg} ${LAYOUT_SHELL[layout]} ${loading ? "is-pulse-loading" : ""}`}
    >
      <div
        className={`pulse-duo-copy z-10 flex flex-col justify-start ${duoConfig.title}`}
      >
        {card.is_update && (
          <span className="mb-2 inline-block w-fit rounded bg-[#003F28]/10 px-2 py-0.5 text-xs font-semibold text-[#003F28]">
            ACTUALIZACIÓN
          </span>
        )}
        <h3 className={`font-medium ${TITLE_SIZE.duo}`}>{card.title}</h3>
      </div>

      {duoConfig.showImage &&
        (imageUrl ? (
          <div
            className={`pulse-duo-media pulse-card-media relative z-0 overflow-hidden rounded-[12px] ${duoConfig.mediaBg}`}
          >
            <img
              src={imageUrl}
              alt=""
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
            {verifiedVideoPicto(card)}
          </div>
        ) : (
          !loading && (
            <div className="relative z-0">
              <ImagePlaceholder
                src={null}
                className={`pulse-duo-media rounded-[12px] opacity-30 ${duoConfig.mediaBg}`}
              />
              {verifiedVideoPicto(card)}
            </div>
          )
        ))}
      {!duoConfig.showImage ? verifiedVideoPicto(card) : null}

      {duoIndex === 1 && summarySnippet && (
        <p className={`pulse-duo-summary z-10 ${duoConfig.summary}`}>{summarySnippet}</p>
      )}

      <PlusButton variant={variant} onClick={open} layout={layout} slotIndex={slotIndex} />
    </article>
  );
}
